import React, { useState } from "react";
import { X, Link2, Check, Send, Camera } from "lucide-react";
import { toast } from "sonner";
import { sportInfo, formatMatchTime } from "../lib/api";

export default function ShareSheet({ match, host, onClose }) {
  const [copied, setCopied] = useState(false);
  const sport = sportInfo(match.sport);
  const url = `${window.location.origin}/match/${match.match_id}`;
  const remaining = match.max_players - (match.player_ids?.length || 0);
  const caption =
    `🏟️ ${sport.label} · ${match.venue}\n` +
    `📍 ${match.city}\n` +
    `⏰ ${formatMatchTime(match.starts_at)}\n` +
    `${remaining > 0 ? `🎯 Butuh ${remaining} pemain lagi!` : "🔥 Match sudah FULL"}\n\n` +
    `Join di JoinPlay 👉 ${url}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link tersalin!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("[share] clipboard write failed, using fallback:", err?.message);
      // Fallback for browsers without clipboard API or when permission is denied
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        toast.success("Link tersalin!");
      } catch (fallbackErr) {
        console.warn("[share] execCommand copy failed:", fallbackErr?.message);
        toast.error("Gagal copy");
      }
      document.body.removeChild(el);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption tersalin! Tinggal paste ke IG Story / Bio");
    } catch (err) {
      console.warn("[share] copyCaption failed:", err?.message);
      toast.error("Gagal copy");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${sport.label} di ${match.venue}`,
          text: caption,
          url,
        });
      } catch (err) {
        // User cancelled or share unsupported — silent is OK here
        if (err?.name !== "AbortError") {
          console.warn("[share] native share failed:", err?.message);
        }
      }
    } else {
      await copyLink();
    }
  };

  const shareToInstagram = async () => {
    // Instagram doesn't support direct URL-based story share from web.
    // Best UX: copy caption, then try to open the Instagram app — user pastes into story/DM.
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption tersalin! Buka Instagram dan paste ke Story/DM");
    } catch (err) {
      console.warn("[share] IG copy failed:", err?.message);
    }
    // Try opening Instagram app (mobile). Falls back to instagram.com on desktop.
    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    if (isMobile) {
      window.location.href = "instagram://story-camera";
      setTimeout(() => {
        window.open("https://instagram.com", "_blank");
      }, 800);
    } else {
      window.open("https://instagram.com", "_blank");
    }
  };

  const shareToWhatsApp = () => {
    const wa = `https://wa.me/?text=${encodeURIComponent(caption)}`;
    window.open(wa, "_blank");
  };

  const shareToTwitter = () => {
    const tw = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`;
    window.open(tw, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="share-sheet">
      <div
        className="w-full max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <div className="font-heading text-lg font-black tracking-tight">Bagikan Match</div>
            <div className="text-xs text-muted-foreground">Ajak temen lo gabung</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center" data-testid="share-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Preview card */}
        <div className="mx-5 my-2 bg-secondary/60 border border-border rounded-2xl p-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-2xl" style={{ background: sport.color + "22", border: `1px solid ${sport.color}44` }}>{sport.icon}</div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm truncate">{sport.label} · {match.venue}</div>
            <div className="text-xs text-muted-foreground truncate">{formatMatchTime(match.starts_at)} · {match.city}</div>
          </div>
        </div>

        {/* Channels grid */}
        <div className="grid grid-cols-4 gap-2 px-5 pt-3">
          <ChannelBtn testid="share-instagram" onClick={shareToInstagram} label="Instagram" color="#E1306C">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
          </ChannelBtn>
          <ChannelBtn testid="share-whatsapp" onClick={shareToWhatsApp} label="WhatsApp" color="#25D366">
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </ChannelBtn>
          <ChannelBtn testid="share-twitter" onClick={shareToTwitter} label="X" color="#FFFFFF">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </ChannelBtn>
          <ChannelBtn testid="share-native" onClick={nativeShare} label="Lainnya" color="#00E5FF">
            <Send className="h-5 w-5" />
          </ChannelBtn>
        </div>

        {/* Copy link */}
        <div className="px-5 pt-4 pb-3">
          <div className="bg-secondary border border-border rounded-2xl p-1.5 flex items-center gap-2">
            <div className="flex-1 min-w-0 px-3 py-2 text-xs text-muted-foreground truncate">{url}</div>
            <button
              onClick={copyLink}
              data-testid="copy-link"
              className="px-4 py-2 rounded-full bg-primary text-primary-foreground font-bold text-xs flex items-center gap-1 active:scale-95"
            >
              {copied ? <><Check className="h-3.5 w-3.5" /> Tersalin</> : <><Link2 className="h-3.5 w-3.5" /> Salin</>}
            </button>
          </div>
        </div>

        {/* Copy caption helper */}
        <div className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
          <button
            onClick={copyCaption}
            data-testid="copy-caption"
            className="w-full text-xs font-bold text-primary inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-primary/10 border border-primary/20 active:scale-95"
          >
            <Camera className="h-3.5 w-3.5" /> Salin caption siap-pakai untuk IG Story / Bio
          </button>
        </div>
      </div>
    </div>
  );
}

function ChannelBtn({ onClick, label, color, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-secondary border border-border hover:border-primary/40 active:scale-95 transition-all"
    >
      <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ color, background: color + "1A", border: `1px solid ${color}40` }}>
        {children}
      </div>
      <span className="text-[10px] font-bold">{label}</span>
    </button>
  );
}
